/**
 * O contrato HTTP da Fase 1, em um lugar só.
 *
 * Cada caminho é relativo a `VITE_API_BASE_URL` (`/api` em dev, atrás do proxy
 * do Vite; o mesmo host em produção, via nginx). O prefixo global da API é
 * `API_GLOBAL_PREFIX=api`, então `/auth/login` daqui é `POST /api/auth/login`
 * no NestJS.
 *
 * Existe como arquivo próprio para que a superfície que o painel consome seja
 * legível de uma vez — quando a API mudar um caminho, o diff é aqui, e não
 * espalhado por seis telas.
 */

/** Autenticação e identidade. */
export const ROTAS_DE_AUTENTICACAO = {
  /** `POST` — e-mail + senha. Devolve sessão OU desafio de 2FA. */
  login: '/auth/login',
  /** `POST` — segunda etapa: código do autenticador ou de recuperação. */
  desafio2fa: '/auth/2fa',
  /** `POST` — troca o refresh (cookie httpOnly) por um access token novo. */
  refresh: '/auth/refresh',
  /** `POST` — encerra a sessão corrente (ou todas). */
  logout: '/auth/logout',
  /** `GET` — provedores de login social habilitados NESTE ambiente. */
  provedores: '/auth/providers',
  /** `POST` — pede o link de redefinição. A resposta é sempre a mesma. */
  esqueciSenha: '/auth/password/forgot',
  /** `POST` — define a nova senha com o token que veio no e-mail. */
  redefinirSenha: '/auth/password/reset',
  /** `POST` — inicia a configuração do TOTP (segredo + QR + códigos). */
  setupTotp: '/auth/2fa/setup',
  /** `POST` — confirma o TOTP com o primeiro código e reemite a sessão. */
  confirmarTotp: '/auth/2fa/confirm',
} as const

/**
 * Início do fluxo OAuth. É NAVEGAÇÃO do navegador, não fetch: a API responde
 * 302 para o provedor, guardando `state` e PKCE do seu lado. No retorno ela
 * grava o cookie de refresh e devolve o navegador para `/login/retorno`.
 */
export function rotaDeAutorizacaoOAuth(provedor: 'GOOGLE' | 'APPLE'): string {
  return `/auth/oauth/${provedor.toLowerCase()}/authorize`
}

/** Usuários (equipe interna e condutores). */
export const ROTAS_DE_USUARIOS = {
  lista: '/users',
  porId: (id: string): string => `/users/${id}`,
  /** `POST` — reenvia o convite de definição de senha. */
  convite: (id: string): string => `/users/${id}/invite`,
} as const

/** Contas de cliente (PF e PJ). */
export const ROTAS_DE_CONTAS = {
  lista: '/accounts',
  porId: (id: string): string => `/accounts/${id}`,
  /** `GET`/`POST` — condutores autorizados de uma conta PJ. */
  condutores: (id: string): string => `/accounts/${id}/drivers`,
} as const

/**
 * Categorias — o catálogo que o cliente aluga.
 *
 * A regra de ouro do domínio mora aqui: o cliente escolhe CATEGORIA, e é por
 * isso que preço de referência, caução e franquia de km estão neste recurso e
 * não em `/vehicles`.
 */
export const ROTAS_DE_CATEGORIAS = {
  lista: '/categories',
  /** `GET` traz a distribuição da frota por status; `PATCH` edita; `DELETE` desativa. */
  porId: (id: string): string => `/categories/${id}`,
} as const

/** Locais de retirada e devolução (sede, filial, aeroporto, ponto de entrega). */
export const ROTAS_DE_LOCAIS = {
  lista: '/locations',
  /** `DELETE` desativa e devolve quantos veículos ficaram sem base. */
  porId: (id: string): string => `/locations/${id}`,
} as const

/** Frota — veículos, trilha de status, plano de revisão e vencimentos. */
export const ROTAS_DE_VEICULOS = {
  lista: '/vehicles',
  /** `GET` traz plano e premissas resolvidas; `DELETE` baixa (não apaga). */
  porId: (id: string): string => `/vehicles/${id}`,
  /** `POST` muda o status com motivo; `GET` lê a trilha, que é append-only. */
  status: (id: string): string => `/vehicles/${id}/status`,
  /** `PUT` — o plano é 1-1 com o veículo, então cria ou atualiza. */
  planoDeManutencao: (id: string): string => `/vehicles/${id}/maintenance-plan`,
  /**
   * `GET` — blindagem, apólice, IPVA, licenciamento e revisão vencendo.
   * Caminho fixo declarado ANTES de `:id` na API; aqui ele é literal e não
   * colide com `porId` porque nenhum id é `alertas`.
   */
  alertasDeVencimento: '/vehicles/alerts/expirations',
} as const

/** Custos de frota e a política de custo indireto do tenant. */
export const ROTAS_DE_CUSTOS = {
  lista: '/costs',
  /** `PATCH` corrige só os campos descritivos; `DELETE` estorna de verdade. */
  porId: (id: string): string => `/costs/${id}`,
  /** `GET`/`PUT` — vida útil, residual, custo de capital e overhead mensal. */
  politica: '/costs/policy',
  /** `POST` — fechamento mensal, idempotente por competência. */
  fechamento: '/costs/closing',
  /** `GET` — custo por veículo numa competência: caixa e competência lado a lado. */
  resumo: '/costs/summary',
} as const

/**
 * Preços — tabelas, tarifas por faixa, as quatro camadas de regra e a política
 * de locação.
 *
 * A política mora num controller próprio (`/rental-policy`, sem o
 * prefixo `/pricing`) e mesmo assim está listada aqui: ela é a decisão de preço
 * mais consequente do sistema — tolerância, buffer, overbooking e validade da
 * cotação —, e separá-la em outro objeto só faria quem procura "onde ficam as
 * regras de preço" ter de olhar em dois lugares.
 */
export const ROTAS_DE_PRECOS = {
  /** `GET` — quantas diárias o período dá, com a tolerância que valeria hoje. */
  simularDiarias: '/pricing/simulate-days',

  tabelas: '/pricing/tables',
  /** `GET` lê; `PATCH` edita; `DELETE` desativa (não apaga — cotação antiga aponta para ela). */
  tabelaPorId: (id: string): string => `/pricing/tables/${id}`,
  /**
   * `GET` traz as tarifas da tabela agrupadas por categoria, com as faixas que
   * FALTAM; `PUT` grava todas as faixas de UMA categoria de uma vez — "a diária
   * não sobe ao alongar" é propriedade do conjunto, não de uma linha.
   */
  tarifasDaTabela: (id: string): string => `/pricing/tables/${id}/rates`,
  /** `DELETE` — tira a categoria da tabela, todas as faixas juntas. */
  tarifasDaCategoria: (id: string, categoryId: string): string =>
    `/pricing/tables/${id}/rates/${categoryId}`,
  /** `GET` — listagem plana de tarifas, filtrável por tabela, categoria e faixa. */
  tarifas: '/pricing/rates',

  /** Camada 2 — alta temporada, feriado, Réveillon. */
  sazonalidades: '/pricing/seasons',
  sazonalidadePorId: (id: string): string => `/pricing/seasons/${id}`,

  /** Camada 3 — sexta e sábado mais caros. */
  diasDaSemana: '/pricing/weekdays',
  diaDaSemanaPorId: (id: string): string => `/pricing/weekdays/${id}`,

  /** Camada 4 — early bird e last minute. */
  antecedencias: '/pricing/lead-times',
  antecedenciaPorId: (id: string): string => `/pricing/lead-times/${id}`,

  /** Camada 5 — preço por ocupação prevista. Desligada por padrão na política. */
  ocupacoes: '/pricing/occupancy',
  ocupacaoPorId: (id: string): string => `/pricing/occupancy/${id}`,

  /** `GET`/`PUT` — tolerância, buffer, overbooking e validade da cotação. */
  politicaDeLocacao: '/rental-policy',
} as const

/** Extras — proteção, condutor adicional, motorista, segurança, acessório. */
export const ROTAS_DE_EXTRAS = {
  lista: '/extras',
  /** `GET` lê; `PATCH` edita; `DELETE` tira da vitrine (desativa). */
  porId: (id: string): string => `/extras/${id}`,
} as const

/** Cupons de desconto. */
export const ROTAS_DE_CUPONS = {
  lista: '/coupons',
  porId: (id: string): string => `/coupons/${id}`,
  /**
   * `POST` — confere o cupom sem consumi-lo. É `POST` numa operação que não
   * muda nada de propósito: código de cupom em query string cai no log do
   * proxy, no histórico do navegador e no `Referer` — campanha vazada.
   */
  validar: '/coupons/validate',
} as const

/**
 * Disponibilidade — capacidade por categoria, calendário, bloqueios de veículo
 * e a escala dos prestadores.
 *
 * `capacidade` responde POR CATEGORIA, nunca por veículo: a regra de ouro vale
 * também aqui, e a placa só é decidida na alocação.
 */
export const ROTAS_DE_DISPONIBILIDADE = {
  /** `GET` — quantas unidades de cada categoria sobram no período (com buffer). */
  capacidade: '/availability/capacity',
  /** `GET` — mapa dia a dia de UMA categoria. Sem buffer: o cliente escolhe dia, não hora. */
  calendario: '/availability/calendar',
  /** `GET` — quantos motoristas e seguranças estão livres no período. */
  prestadoresLivres: '/availability/providers',

  bloqueios: '/availability/blocks',
  /** `GET` lê; `PATCH` move; `DELETE` libera o veículo — aqui a linha SAI mesmo. */
  bloqueioPorId: (id: string): string => `/availability/blocks/${id}`,

  prestadores: '/providers',
  /** `GET` lê; `PATCH` edita (inclusive reativa); `DELETE` inativa. */
  prestadorPorId: (id: string): string => `/providers/${id}`,
  /**
   * `GET`/`POST` — os períodos em que cada prestador está ocupado. Caminho fixo
   * declarado ANTES de `:id` na API; aqui não colide porque nenhum id é
   * `bloqueios`.
   */
  escala: '/providers/blocks',
  /** `DELETE` — não há `PATCH` de escala: quem errou o horário apaga e cria. */
  escalaPorId: (id: string): string => `/providers/blocks/${id}`,
} as const

/**
 * Cotação, lista de espera e overbooking.
 *
 * `POST /quotes` ESCREVE: cada categoria cotável vira um `QuoteSnapshot`
 * congelado, com o hash das regras aplicadas. Por isso 201, e por isso a
 * resposta continua sendo 201 mesmo quando nada está disponível — "não há
 * carro" é leitura de mercado, não falha da requisição.
 */
export const ROTAS_DE_COTACOES = {
  lista: '/quotes',
  porId: (id: string): string => `/quotes/${id}`,
  /**
   * `GET`/`POST` — a demanda reprimida (Gap 12). O slug é português; o recurso
   * da permissão continua sendo `waitlist`.
   */
  listaDeEspera: '/waitlist',
  /** `GET`/`POST` — overbooking exercido e o custo do upgrade (Gap 11). */
  overbooking: '/overbooking',
} as const

/**
 * Reservas — a máquina de estados, a fila de aprovação e o fechamento (Fase 4).
 *
 * Aprovar e recusar têm caminhos próprios porque têm PERMISSÃO própria
 * (`reservation.approve`); as demais transições passam por `transicoes` e são
 * validadas contra a máquina de `@locaveri/shared`.
 */
export const ROTAS_DE_RESERVAS = {
  lista: '/reservations',
  /** Caminho fixo declarado ANTES de `:id` na API. */
  filaDeAprovacao: '/reservations/approval-queue',
  porId: (id: string): string => `/reservations/${id}`,
  transicoes: (id: string): string => `/reservations/${id}/transitions`,
  aprovar: (id: string): string => `/reservations/${id}/approve`,
  recusar: (id: string): string => `/reservations/${id}/decline`,
  prorrogar: (id: string): string => `/reservations/${id}/extend`,
  solicitarDocumento: (id: string): string => `/reservations/${id}/request-document`,
  /** `POST` — a mesma conta do fechamento, sem gravar nada. */
  simularFechamento: (id: string): string => `/reservations/${id}/settlement/simulate`,
  /** `POST` — grava o fechamento IMUTÁVEL e finaliza a reserva. */
  fechar: (id: string): string => `/reservations/${id}/close`,
} as const

/**
 * Análise cadastral (Mira Score) — a chave da locadora e a análise da reserva.
 * `daReserva` é `GET` (ler) e `POST` (pedir à mão, com custo).
 */
export const ROTAS_DA_ANALISE_CADASTRAL = {
  integracao: '/integrations/mira-score',
  testarIntegracao: '/integrations/mira-score/test',
  daReserva: (reservaId: string): string => `/reservations/${reservaId}/credit-analysis`,
} as const

/** Alocação — candidatos pontuados, alocar/desfazer, pesos e o Gantt. */
export const ROTAS_DE_ALOCACOES = {
  candidatos: (reservaId: string): string => `/reservations/${reservaId}/allocation/candidates`,
  /** `POST` — sem `vehicleId` o algoritmo escolhe; com, exige justificativa. */
  alocar: (reservaId: string): string => `/reservations/${reservaId}/allocation`,
  desfazer: (reservaId: string): string => `/reservations/${reservaId}/allocation/undo`,
  /**
   * `POST` — troca no meio da locação (Adendo C): o corpo leva o motivo e as
   * DUAS vistorias (retorno do carro que sai, saída do que entra).
   */
  trocar: (reservaId: string): string => `/reservations/${reservaId}/allocation/swap`,
  /** `GET`/`PUT` — os pesos do scoring (política do gestor). */
  pesos: '/allocations/weights',
  /** `GET` — o calendário da frota, janela de 1 a 92 dias. */
  gantt: '/allocations/gantt',
} as const

/** Vistorias — checklist configurável, registro, anulação e o comparativo. */
export const ROTAS_DE_VISTORIAS = {
  /** `GET`/`PUT` — os itens que o pátio confere (os ângulos de foto são fixos). */
  checklist: '/inspections/checklist',
  daReserva: (reservaId: string): string => `/reservations/${reservaId}/inspections`,
  comparativo: (reservaId: string): string => `/reservations/${reservaId}/inspections/comparison`,
  anular: (reservaId: string, vistoriaId: string): string =>
    `/reservations/${reservaId}/inspections/${vistoriaId}/void`,
} as const

/** Contratos — geração com hash, assinatura (Clicksign/manual) e download. */
export const ROTAS_DE_CONTRATOS = {
  daReserva: (reservaId: string): string => `/reservations/${reservaId}/contracts`,
  /** `GET`/`PUT` — o template HTML com variáveis, um por tenant. */
  template: '/contracts/template',
  download: (id: string): string => `/contracts/${id}/download`,
  enviarParaAssinatura: (id: string): string => `/contracts/${id}/send-for-signature`,
  registrarAssinatura: (id: string): string => `/contracts/${id}/register-signature`,
  cancelar: (id: string): string => `/contracts/${id}/cancel`,
} as const

/** Multas de trânsito (Gap 4) — a vinculação à reserva é da API, por placa+período. */
export const ROTAS_DE_MULTAS = {
  lista: '/traffic-fines',
  porId: (id: string): string => `/traffic-fines/${id}`,
  indicarCondutor: (id: string): string => `/traffic-fines/${id}/identify-driver`,
  /** `POST` — a multa que chegou depois do fechamento: caução retida ou cartão. */
  cobrarDoCliente: (id: string): string => `/traffic-fines/${id}/charge`,
} as const

/** Sinistros (Adendo D). */
export const ROTAS_DE_SINISTROS = {
  lista: '/incidents',
  porId: (id: string): string => `/incidents/${id}`,
} as const

/**
 * Pagamentos (Fase 5). Criação de cobrança exige `Idempotency-Key`
 * (invariante nº 9); capturar/baixar/estornar são gesto de FINANCEIRO+.
 */
export const ROTAS_DE_PAGAMENTOS = {
  daReserva: (reservaId: string): string => `/reservations/${reservaId}/payments`,
  /** `POST` — a venda das diárias (total congelado; cartão tokenizado ou PIX). */
  venda: (reservaId: string): string => `/reservations/${reservaId}/payments/sale`,
  /** `POST` — a pré-autorização da caução (bloqueio, não cobrança). */
  caucao: (reservaId: string): string => `/reservations/${reservaId}/payments/deposit`,
  porId: (id: string): string => `/payments/${id}`,
  capturar: (id: string): string => `/payments/${id}/capture`,
  baixar: (id: string): string => `/payments/${id}/void`,
  estornar: (id: string): string => `/payments/${id}/refund`,
  renovar: (id: string): string => `/payments/${id}/renew`,
  sincronizar: (id: string): string => `/payments/${id}/sync`,
  /** `GET` — cauções na janela de renovação ou vencidas (Gap 6). */
  renovacoesPendentes: '/payments/renewals-due',
  /** `GET` — o relatório da reconciliação diária. */
  reconciliacao: '/payments/reconciliation',
  lista: '/payments',
} as const

/** Faturas mensais do corporativo (Adendo C) e notas fiscais ANEXADAS. */
export const ROTAS_DE_FATURAS = {
  lista: '/invoices',
  porId: (id: string): string => `/invoices/${id}`,
  fechar: (id: string): string => `/invoices/${id}/close`,
  marcarPaga: (id: string): string => `/invoices/${id}/mark-paid`,
  cancelar: (id: string): string => `/invoices/${id}/cancel`,
  notasDaFatura: (id: string): string => `/invoices/${id}/fiscal-notes`,
  /** `POST` — vincula o XML emitido FORA (decisão nº 4: anexar, não emitir). */
  anexarNota: '/fiscal-notes',
  notasDaReserva: (reservaId: string): string => `/reservations/${reservaId}/fiscal-notes`,
} as const

/**
 * Documentos pedidos ao cliente (Gap 2) — o ciclo solicitar → anexar →
 * avaliar. A SOLICITAÇÃO fica em `ROTAS_DE_RESERVAS.solicitarDocumento`; aqui
 * moram o resto do ciclo e a listagem.
 */
export const ROTAS_DE_DOCUMENTOS = {
  daReserva: (reservaId: string): string => `/reservations/${reservaId}/documents`,
  anexar: (reservaId: string, documentoId: string): string =>
    `/reservations/${reservaId}/documents/${documentoId}/attach`,
  avaliar: (reservaId: string, documentoId: string): string =>
    `/reservations/${reservaId}/documents/${documentoId}/review`,
} as const

/**
 * Rastreamento 3S (Gap 5). A posição vem do NOSSO banco (o polling espelha a
 * 3S); toda consulta fica na trilha de auditoria, e o endereço só vem com
 * `?revelarEndereco=true` — que também é auditado.
 */
export const ROTAS_DE_RASTREAMENTO = {
  sincronizarFrota: '/tracking/sync-fleet',
  sincronizarPosicoes: '/tracking/sync-positions',
  /** A última posição de cada carro rastreado — do banco, auditada por abertura. */
  mapaDaFrota: '/tracking/fleet-positions',
  posicao: (vehicleId: string): string => `/tracking/vehicles/${vehicleId}/position`,
  ocorrencias: (vehicleId: string): string => `/tracking/vehicles/${vehicleId}/occurrences`,
} as const

/**
 * Upload por URL assinada — o binário nunca passa pela API. Cada finalidade tem
 * rota própria porque tem permissão própria.
 */
export const ROTAS_DE_ARQUIVOS = {
  /**
   * `POST` — assina o upload de uma foto de CATEGORIA (a galeria da vitrine).
   * Fotos da classe, nunca do carro específico: o cliente aluga categoria e não
   * deve reconhecer a placa que vai receber.
   */
  fotoDeCategoria: '/files/category-photo',
  fotoDeVistoria: '/files/inspection-photo',
  assinaturaDeVistoria: '/files/inspection-signature',
  fotoDeSinistro: '/files/incident-photo',
  documentoDeCliente: '/files/customer-document',
  xmlDeNotaFiscal: '/files/fiscal-note-xml',
  /**
   * `POST` — troca uma CHAVE gravada por uma URL de leitura assinada. O bucket
   * é privado: toda exibição de anexo (foto de vistoria, documento) passa por
   * aqui, e a permissão conferida é a da PASTA da chave.
   */
  urlDeLeitura: '/files/read-url',
} as const

/**
 * A vitrine — o recorte que o CLIENTE vê.
 *
 * Superfície separada da listagem administrativa de propósito: a de lá traz
 * `ativo`, contagem de frota e premissas de depreciação; a daqui traz só o que
 * o cliente pode ver. `/extras/storefront` nasce no controller de extras e mesmo
 * assim está listado aqui — o que agrupa os dois caminhos é a regra de
 * exposição, não o controller de origem.
 */
export const ROTAS_DE_VITRINE = {
  /** `GET` — categorias ativas, sem placa, sem chassi e sem contagem de frota. */
  categorias: '/storefront/categories',
  /** `GET` — extras ativos, sem os campos de administração. Sem paginação. */
  extras: '/extras/storefront',
} as const

/**
 * Controle de acesso — papéis e permissões.
 *
 * A concessão vive em TABELA (`role_permissions`), e não em `if` no código:
 * `PERMISSOES_POR_PAPEL` do pacote compartilhado é só o padrão que o seed
 * instala. É por isso que existe uma tela para editá-la — o gestor decide o que
 * cada perfil acessa sem deploy.
 */
export const ROTAS_DE_RBAC = {
  /** `GET` — papéis do tenant com a concessão de HOJE e quantos usuários têm cada um. */
  papeis: '/rbac/roles',
  /** `GET` — o catálogo do que EXISTE para conceder, com a descrição de cada item. */
  permissoes: '/rbac/permissions',
  /** `GET` — as permissões efetivas de quem pergunta, lidas do banco e não do token. */
  minhasPermissoes: '/rbac/me',
  /** `PUT` — substituição INTEGRAL da concessão de um papel. */
  permissoesDoPapel: (codigo: string): string =>
    `/rbac/roles/${encodeURIComponent(codigo)}/permissions`,
} as const

/**
 * Pesquisa de satisfação e motivos de cancelamento (Fase 6).
 *
 * A rota PÚBLICA da pesquisa (`/public/surveys/:token`) NÃO está aqui de
 * propósito: ela é do SITE, respondida sem sessão por quem recebeu o link. O
 * painel só configura e lê o relatório.
 */
export const ROTAS_DE_PESQUISA = {
  /** `GET` e `PUT` — a configuração inteira. Nunca 404: sem registro, o padrão. */
  configuracao: '/surveys/config',

  /** `GET` — o agregado, com a taxa de resposta e o sinal `confiavel`. */
  relatorio: (filtro: {
    readonly de?: string | undefined
    readonly ate?: string | undefined
    readonly locationId?: string | undefined
  }): string => {
    // ⚠️ STRING VAZIA NÃO É "AUSENTE" — e a diferença aparece na tela.
    //
    // O campo `<input type="date">` devolve `''` quando a pessoa limpa a data
    // (ou digita algo incompleto), e `filtro.de !== undefined` deixava isso
    // virar `?de=`. A API valida com `z.iso.date()`, recusa a string vazia com
    // 422, e o relatório inteiro sumia da tela — trocado por um erro de
    // validação, num gesto que a pessoa lê como "limpei o filtro".
    const consulta = new URLSearchParams()
    if (filtro.de !== undefined && filtro.de !== '') {
      consulta.set('de', filtro.de)
    }
    if (filtro.ate !== undefined && filtro.ate !== '') {
      consulta.set('ate', filtro.ate)
    }
    if (filtro.locationId !== undefined && filtro.locationId !== '') {
      consulta.set('locationId', filtro.locationId)
    }
    const texto = consulta.toString()
    return texto === '' ? '/surveys/report' : `/surveys/report?${texto}`
  },

  /** `GET` — o catálogo de motivos. Sem `origem`, devolve tudo. */
  motivos: (opcoes: {
    readonly incluirInativos?: boolean | undefined
    readonly origem?: 'CLIENTE' | 'EMPRESA' | undefined
  }): string => {
    const consulta = new URLSearchParams()
    if (opcoes.incluirInativos === true) {
      consulta.set('incluirInativos', 'true')
    }
    if (opcoes.origem !== undefined) {
      consulta.set('origem', opcoes.origem)
    }
    const texto = consulta.toString()
    return texto === '' ? '/cancellation-reasons' : `/cancellation-reasons?${texto}`
  },

  /** `POST` — cria um motivo. */
  criarMotivo: '/cancellation-reasons',

  /** `PATCH` — edita. Não existe DELETE: `ativo: false` é a exclusão daqui. */
  motivo: (id: string): string => `/cancellation-reasons/${encodeURIComponent(id)}`,
} as const

/**
 * BI — as três visões determinísticas (Fase 7).
 *
 * ⚠️ Data VAZIA nunca vira `?de=` — a API valida com `z.iso.date()` e recusaria
 * a string vazia com 422, trocando o relatório inteiro por um erro de validação
 * num gesto que a pessoa lê como "limpei o filtro". Foi o defeito que a revisão
 * da Fase 6 achou no relatório de NPS.
 */
export const ROTAS_DE_BI = {
  frota: (janela: JanelaDoBi): string => montarRotaDeBi('/metrics/fleet', janela),
  /** A frota SEM dinheiro — chave própria (`fleet_occupancy.read`), para a operação. */
  ocupacao: (janela: JanelaDoBi): string => montarRotaDeBi('/metrics/fleet-occupancy', janela),
  demanda: (janela: JanelaDoBi): string => montarRotaDeBi('/metrics/demand', janela),
  funil: (janela: JanelaDoBi): string => montarRotaDeBi('/metrics/funnel', janela),
  /**
   * A exportação. `visao` e `formato` entram junto da janela — a mesma rota
   * serve os dois formatos, e a permissão (`report.export`) é uma só.
   */
  exportar: (janela: JanelaDoBi, visao: VisaoExportavel, formato: FormatoDeExportacao): string =>
    montarRotaDeBi('/metrics/export', janela, { visao, formato }),
} as const

/**
 * A camada de IA. Note que `gerar` e `insight` são o MESMO caminho com verbos
 * diferentes — e permissões diferentes na API (`ai_insight.read` contra
 * `ai_insight.create`), porque ver o texto e queimar o teto de tokens do tenant
 * são capacidades distintas.
 */
export const ROTAS_DE_IA = {
  insight: (tipo: string): string => `/ai/insights/${encodeURIComponent(tipo)}`,
  gerar: (tipo: string): string => `/ai/insights/${encodeURIComponent(tipo)}`,
  feedback: (insightId: string): string => `/ai/insights/${encodeURIComponent(insightId)}/feedback`,
  consumo: '/ai/usage',
} as const

interface JanelaDoBi {
  readonly de?: string | undefined
  readonly ate?: string | undefined
  readonly categoryId?: string | undefined
  readonly locationId?: string | undefined
}

export type VisaoExportavel = 'fleet' | 'demand' | 'funnel'
export type FormatoDeExportacao = 'xlsx' | 'pdf'

function montarRotaDeBi(
  caminho: string,
  janela: JanelaDoBi,
  extras: Readonly<Record<string, string>> = {},
): string {
  const consulta = new URLSearchParams()
  const campos: readonly (keyof JanelaDoBi)[] = ['de', 'ate', 'categoryId', 'locationId']
  for (const nome of campos) {
    const valor = janela[nome]
    if (valor !== undefined && valor !== '') {
      consulta.set(nome, valor)
    }
  }
  for (const [nome, valor] of Object.entries(extras)) {
    consulta.set(nome, valor)
  }
  const texto = consulta.toString()
  return texto === '' ? caminho : `${caminho}?${texto}`
}
